/**
 * PostgREST column for global `contains` search when the table has no
 * `name` / `title` / `*_name` column in the default heuristic.
 */
export const RESOURCE_SEARCH_FIELD: Record<string, string> = {
  user_tokens: 'token',
  identity: 'connection',
  tokens: 'token',
  blacklisted_emails: 'email_domain',
  greenhouse_request_errors: 'api_call',
  public_api_request_logs: 'endpoint',
  partner_api_keys: 'customer_name',
  partner_assessments: 'partner_test_name',
  whatsapp_messages: 'body',
  email_history: 'subject',
  newsletter_subscriptions: 'email',
  company_templates: 'name',
  product_demo_requests: 'company_name',
  public_api_contact_requests: 'email',
  public_api_signing_challenges: 'public_key',
  onboarding_services: 'service_name',
  voice_over_audit_logs: 'text_content',
  candidate_data_transferred_fields: 'fields_type',
  onboarding_service_transfer_history: 'transfer_status',
  account_feature_overrides: 'feature_key',
  role_type_definitions: 'role_type',
  permission_change_audits: 'action',
}

/**
 * Maps resource display names to actual PostgREST/DB table names when they differ.
 * Key = resource display name used in UI/routes; Value = real table name for API queries.
 */
export const RESOURCE_TABLE_NAME: Record<string, string> = {
  company_email_configurations: 'company_email_configuration',
  organization_services_count: 'services_count',
  add_on_service_packages: 'add_on_packages',
  greenhouse_partner_api_keys: 'partner_api_keys',
  greenhouse_partner_assessments: 'partner_assessments',
  onboarding_service_transfer_histories: 'onboarding_service_transfer_history',
  email_histories: 'email_history',
}

export const RESOURCE_GROUPS: Record<string, string[]> = {
  Users: ['users', 'identity', 'user_tokens'],
  Companies: ['companies', 'branches', 'departments', 'company_users', 'company_roles', 'company_email_configuration'],
  Agencies: ['agencies', 'services_count'],
  Candidates: [
    'local_candidates', 'job_seekers', 'candidate_educations', 'candidate_experiences',
    'candidate_courses', 'candidate_projects', 'candidate_languages', 'candidate_documents',
  ],
  'Positions & Requisitions': [
    'company_positions', 'position_stages', 'position_questions',
    'position_assigned_users', 'evaluation_weights', 'requisitions', 'approval_requests',
  ],
  'Applications & Interviews': [
    'applications', 'interview_versions', 'interview_questions',
    'evaluation_forms', 'voice_over_rate_limits', 'voice_over_audit_logs',
  ],
  Billing: ['subscriptions', 'plans', 'tokens', 'add_on_packages', 'offer_letters'],
  Capabilities: ['account_feature_overrides'],
  'Access Control': ['permission_change_audits'],
  Integrations: [
    'partner_api_keys', 'partner_assessments', 'greenhouse_request_errors',
    'onboarding_services', 'onboarding_service_transfer_history',
    'candidate_data_transferred_fields', 'organization_assigned_users',
  ],
  Communications: [
    'whatsapp_messages', 'newsletter_subscriptions', 'blacklisted_emails',
    'email_history', 'company_templates',
  ],
  'Public API': [
    'product_demo_requests', 'public_api_contact_requests',
    'public_api_signing_challenges', 'public_api_request_logs',
  ],
}

/**
 * Multi-field search arrays for resources that need OR-based global search.
 * When defined, DataTable builds an OR filter across all listed fields.
 */
export const RESOURCE_SEARCH_FIELDS: Record<string, string[]> = {
  company_email_configuration: ['sender_email', 'sender_name', 'mail_username'],
  applications: ['status', 'applied_at'],
  interview_versions: ['type', 'status'],
  interview_questions: ['question_text', 'type'],
  evaluation_forms: ['name', 'status'],
  evaluation_weights: ['name', 'category'],
  approval_requests: ['status', 'request_type'],
  voice_over_rate_limits: ['language', 'provider'],
  subscriptions: ['status'],
  offer_letters: ['status'],
  onboarding_service_transfer_history: ['transfer_status', 'service_name'],
  organization_assigned_users: ['email', 'username'],
  candidate_educations: ['institution', 'field_of_study'],
  candidate_languages: ['language', 'proficiency'],
  position_questions: ['question_text'],
  position_assigned_users: ['email', 'username'],
}

export const resources = Object.entries(RESOURCE_GROUPS).flatMap(([group, tables]) =>
  tables.map((name) => {
    const searchField = RESOURCE_SEARCH_FIELD[name]
    const searchFields = RESOURCE_SEARCH_FIELDS[name]
    return {
      name,
      meta: {
        group,
        ...(searchFields ? { searchFields } : searchField ? { searchField } : {}),
      },
      list: `/${name}`,
      create: `/${name}/create`,
      edit: `/${name}/edit/:id`,
      show: `/${name}/show/:id`,
    }
  })
)

/**
 * Maps each resource to its PostgreSQL read view for list and show queries.
 * Forms (create/edit/delete) continue using the original resource name.
 */
export const RESOURCE_READ_VIEW: Record<string, string> = {
  email_history: 'email_history_view',
  // Companies
  branches: 'branches_view',
  departments: 'departments_view',
  company_users: 'company_users_view',
  company_roles: 'company_roles_view',
  company_email_configuration: 'company_email_configuration_view',
  // Candidates
  local_candidates: 'local_candidates_view',
  job_seekers: 'job_seekers_view',
  candidate_educations: 'candidate_educations_view',
  candidate_experiences: 'candidate_experiences_view',
  candidate_courses: 'candidate_courses_view',
  candidate_projects: 'candidate_projects_view',
  candidate_languages: 'candidate_languages_view',
  candidate_documents: 'candidate_documents_view',
  // Positions & Requisitions
  company_positions: 'company_positions_view',
  position_stages: 'position_stages_view',
  position_questions: 'position_questions_view',
  position_assigned_users: 'position_assigned_users_view',
  evaluation_weights: 'evaluation_weights_view',
  requisitions: 'requisitions_view',
  approval_requests: 'approval_requests_view',
  // Applications & Interviews
  applications: 'applications_view',
  interview_versions: 'interview_versions_view',
  voice_over_rate_limits: 'voice_over_rate_limits_view',
  voice_over_audit_logs: 'voice_over_audit_logs_view',
  offer_letters: 'offer_letters_view',
  // Billing
  subscriptions: 'subscriptions_view',
  // Integrations
  partner_api_keys: 'partner_api_keys_view',
  partner_assessments: 'partner_assessments_view',
  onboarding_services: 'onboarding_services_view',
  onboarding_service_transfer_history: 'onboarding_service_transfer_history_view',
  candidate_data_transferred_fields: 'candidate_data_transferred_fields_view',
  organization_assigned_users: 'organization_assigned_users_view',
  // Communications
  whatsapp_messages: 'whatsapp_messages_view',
  company_templates: 'company_templates_view',
}
